<!--
  ZoneTable part — the Filters trigger and the surface it opens. INTERNAL: not a Zone component, no
  story, not in llms.txt. Rendered only by Table.vue, whose guide documents the model under FILTERS.

  ONE trigger, two possible surfaces: a side DRAWER (the default) or an anchored FLOATING MENU
  (`placement: 'panel'` on every non-inline filter). Which one is Table.vue's decision — see
  `filterSurface` there — because a button can only open one thing, so it is a property of the
  table rather than of a filter.

  The trigger is spelled out twice because each surface needs it inside its own `as-child` slot,
  and that is now the only duplication here: the FIELDS come from FilterFields.vue, rendered once
  per surface from one definition.
-->

<template>
  <!--
    DRAWER — the default. ZoneDrawer supplies the header, the × and the scrim, so the title lives
    in `title` and Reset goes in its #actions slot (between the title and the ×) rather than in a
    header this component draws itself. Its body carries no padding of its own (see Drawer.vue), so
    the fields bring the 16px.
  -->
  <ZoneDrawer
    v-if="surface === 'drawer'"
    :model-value="open"
    :title="t('title')"
    size="md"
    @update:model-value="$emit('update:open', $event)"
  >
    <template #trigger>
      <ZoneButton
        variant="outline"
        color="gray_alt"
        size="sm"
        leading-icon="filter_list"
        class="shrink-0"
        data-testid="table-filters-trigger"
      >
        {{ t('title') }}
        <template
          v-if="activeCount"
          #indicator
        >{{ activeBadge }}</template>
      </ZoneButton>
    </template>

    <template #actions>
      <button
        v-if="activeCount"
        type="button"
        :class="RESET_CLASSES"
        data-testid="table-filters-reset"
        @click="$emit('reset')"
      >
        <ZoneIcon
          icon="refresh"
          size="xs"
        />
        {{ t('reset') }}
      </button>
    </template>

    <TableFilterFields
      :filters="filters"
      :values="values"
      :set-filter="setFilter"
      :reset="reset"
      class="flex flex-col gap-4 p-4"
      @set="(key, next) => $emit('set', key, next)"
    >
      <template
        v-for="name in forwardedSlots"
        :key="name"
        #[name]="slotProps"
      >
        <slot
          :name="name"
          v-bind="slotProps ?? {}"
        />
      </template>
    </TableFilterFields>
  </ZoneDrawer>

  <!-- FLOATING MENU — opt in with `placement: 'panel'`. Draws its own header row. -->
  <PopoverRoot
    v-else
    :open="open"
    @update:open="$emit('update:open', $event)"
  >
    <PopoverTrigger as-child>
      <ZoneButton
        variant="outline"
        color="gray_alt"
        size="sm"
        leading-icon="filter_list"
        class="shrink-0"
        data-testid="table-filters-trigger"
      >
        {{ t('title') }}
        <template
          v-if="activeCount"
          #indicator
        >{{ activeBadge }}</template>
      </ZoneButton>
    </PopoverTrigger>

    <PopoverPortal to="#zone-teleport">
      <PopoverContent
        align="start"
        :side-offset="6"
        class="z-50 flex max-h-[70vh] w-[320px] flex-col rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-none"
        data-testid="table-filters-panel"
      >
        <div class="flex h-[46px] items-center justify-between border-b border-gray-200 px-4">
          <span class="text-sm font-semibold text-gray-800">{{ t('title') }}</span>
          <button
            v-if="activeCount"
            type="button"
            :class="RESET_CLASSES"
            data-testid="table-filters-reset"
            @click="$emit('reset')"
          >
            <ZoneIcon
              icon="refresh"
              size="xs"
            />
            {{ t('reset') }}
          </button>
        </div>

        <TableFilterFields
          :filters="filters"
          :values="values"
          :set-filter="setFilter"
          :reset="reset"
          class="flex flex-col gap-4 overflow-y-auto p-4"
          @set="(key, next) => $emit('set', key, next)"
        >
          <template
            v-for="name in forwardedSlots"
            :key="name"
            #[name]="slotProps"
          >
            <slot
              :name="name"
              v-bind="slotProps ?? {}"
            />
          </template>
        </TableFilterFields>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<script lang="ts">
import { computed, useSlots, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { PopoverRoot, PopoverTrigger, PopoverPortal, PopoverContent } from 'reka-ui';
import ZoneIcon from '../Icon.vue';
import ZoneButton from '../Button.vue';
import ZoneDrawer from '../Drawer.vue';
import TableFilterFields from './FilterFields.vue';
import type { TableFilter, TableFilterValue } from './types';

/**
 * `text-xs` against the surface title's `text-sm` — one step down the scale — with a leading icon,
 * and rendered ONLY while something is active. It used to sit there permanently in a disabled
 * state, which reads as a control the user has failed to earn rather than one with nothing to do.
 */
const RESET_CLASSES = 'inline-flex items-center gap-1 rounded text-xs font-medium text-primary-600 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneTableFilters' });

const { t } = useI18n();

defineProps({
  /** Which chrome to open. Table.vue decides; see `filterSurface` there. */
  surface: {
    type: String as PropType<'drawer' | 'panel'>,
    required: true,
  },
  open: {
    type: Boolean,
    required: true,
  },
  filters: {
    type: Array as PropType<TableFilter[]>,
    required: true,
  },
  values: {
    type: Object as PropType<Record<string, TableFilterValue[]>>,
    required: true,
  },
  /** How many filters Reset would clear — also the trigger's badge. */
  activeCount: {
    type: Number,
    required: true,
  },
  activeBadge: {
    type: String,
    required: true,
  },
  setFilter: {
    type: Function as PropType<(key: string, values: unknown) => void>,
    required: true,
  },
  reset: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

defineEmits(['update:open', 'reset', 'set']);

const slots = useSlots();

/** Relays the `#filters` slot on to the field list. See Table.vue's `forwardedSlots`. */
const forwardedSlots = computed<string[]>(() => Object.keys(slots));
</script>

<i18n lang="json">
{
  "en": {
    "title": "Filters",
    "reset": "Reset"
  }
}
</i18n>
